/* eslint-disable no-await-in-loop */
import defaultHeaders from './default-headers';

/**
 * Fetch POST request utility
 * @param {string} url - The URL to send the POST request to.
 * @param {any} data - The data to be sent in the request body.
 * @param {boolean} isFileUpload - Whether the request is for a file upload.
 * @param {any} onProgress - Optional callback for progress updates.
 * @returns {any} - The response from the server.
 */
// const postStream = async (
//   url: string,
//   data: any,
//   isFileUpload = false,
//   onProgress?: (progress: any) => void,
// ): Promise<Response> => {
//   try {
//     const customHeaders: HeadersInit = isFileUpload
//       ? {} // FormData automatically sets the headers
//       : { 'Content-Type': 'application/json' };

//     const authToken = defaultHeaders();
//     const headers: HeadersInit = {
//       ...(authToken && authToken),
//       ...customHeaders,
//     };

//     const requestBody = isFileUpload ? data : JSON.stringify(data);

//     const response = await fetch(url, {
//       method: 'POST',
//       headers,
//       body: requestBody,
//     });

//     if (!response.ok) {
//       throw new Error(`HTTP error! Status: ${response.status}`);
//     }

//     // Handle response stream if onProgress callback is provided
//     if (onProgress) {
//       const reader = response.body?.getReader();
//       const decoder = new TextDecoder();
//       let done = false;

//       while (!done && reader) {
//         const { value, done: doneReading } = await reader.read();
//         done = doneReading;

//         const chunk = decoder.decode(value);
//         const updates = chunk.split('\n').filter(Boolean); // Process each new-line delimited message

//         updates.forEach((update) => {
//           try {
//             const parsedUpdate = JSON.parse(update);

//             // Check for backend-sent error and handle it
//             if (parsedUpdate.error) {
//               // Construct custom error object
//               const customError = new Error(parsedUpdate.message);
//               (customError as any).response = {
//                 data: {
//                   message: parsedUpdate.message,
//                 },
//               };
//               throw customError;
//             }

//             onProgress(parsedUpdate); // Call the progress update callback
//           } catch (error) {
//             console.error('Error parsing JSON update:', error);
//             throw error; // Re-throw the error to exit the loop
//           }
//         });
//       }
//     }

//     return response;
//   } catch (error) {
//     console.error('Error in postRequest:', error);
//     throw error;
//   }
// };

const postStream = async (
  url: string,
  data: any,
  isFileUpload = false,
  onProgress?: (progress: any) => void,
): Promise<Response> => {
  const headers: HeadersInit = {
    ...(defaultHeaders() || {}),
    ...(isFileUpload ? {} : { 'Content-Type': 'application/json' }),
  };

  const body = isFileUpload ? data : JSON.stringify(data);
  const response = await fetch(url, { method: 'POST', headers, body });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  if (!onProgress || !response.body) return response;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  /**
   * Loop function
   * @param {any} param0 - Destructured stream read result
   * @param {any} param0.value - The chunk value
   * @param {any} param0.done - Indicates whether streaming is done
   * @returns {any} A promise that resolves when chunk processing finishes
   */
  const processChunk = ({ value, done }: ReadableStreamReadResult<Uint8Array>): Promise<void> => {
    if (done) {
      const remaining = buffer.trim();
      if (remaining) {
        try {
          const parsed = JSON.parse(remaining);
          if (parsed.error) {
            const customError = new Error(parsed.message);
            (customError as any).response = { data: { message: parsed.message } };
            throw customError;
          }
          onProgress(parsed);
        } catch (error) {
          console.error('Error parsing final streamed JSON:', error, 'Raw data:', remaining);
          throw error;
        }
      }
      return Promise.resolve();
    }

    const currentValue = decoder.decode(value || new Uint8Array(), { stream: true });
    buffer += currentValue;

    const boundary = buffer.lastIndexOf('}');
    if (boundary !== -1) {
      const processable = buffer.slice(0, boundary + 1);
      buffer = buffer.slice(boundary + 1);

      const bufferParts = processable
        .split(/(?<=\})\s*(?=\{)/)
        .map((p) => p.trim())
        .filter(Boolean);

      bufferParts.forEach((part) => {
        try {
          const parsed = JSON.parse(part);
          if (parsed.error) {
            const customError = new Error(parsed.message);
            (customError as any).response = { data: { message: parsed.message } };
            throw customError;
          }
          onProgress(parsed);
        } catch (error) {
          console.error('Error parsing streamed JSON part:', error, 'Part:', part);
          throw error;
        }
      });
    }

    // Recursively process next chunk (no loop)
    return reader.read().then(processChunk);
  };

  await reader
    .read()
    .then(processChunk)
    .catch((error) => {
      console.error('Stream processing error:', error);
      throw error;
    });

  return response;
};

export default postStream;
